import {
  VStack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  Menu,
  MenuButton,
  IconButton,
  MenuList,
  MenuItem
} from '@chakra-ui/react'
import NavBar from 'components/NavBar'
import { useSelector } from 'model/hooks'
import {
  IFree,
  IEntityType,
  ILesson,
  ISection,
  ILessonType,
  SectionType
} from 'shared/types'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import get from 'lodash/get'
import Loading from 'components/Loading'
import LessonsList from 'components/LessonsList'
import { dbDeleteFree, dbDuplicateFree, dbUpdateFree } from 'controllers/frees'
import { useContext, useMemo, useRef, useState } from 'react'
import LessonsContext from 'contexts/LessonsContext'
import { generateId } from 'controllers/db'
import { dbCreateLesson, dbDeleteLessonEntity } from 'controllers/lessons'
import filter from 'lodash/filter'
import { CopyIcon, DeleteIcon, SettingsIcon } from '@chakra-ui/icons'
import DeleteConfirmation, {
  IDeleteConfirmationModal
} from 'modals/DeleteConfirmation'
import CustomTabPanel from 'components/CustomTabPanel'
import EntityUsers from 'components/EntityUsers'
import EntityContent from 'components/EntityContent'
import EditSectionDrawer from 'modals/EditSectionDrawer'
import Footer from 'shared/components/Footer'
import FreePage from 'shared/pages/FreePage'
import { storageRef } from 'controllers/db'
import { createSection } from 'controllers/sections'
import { arrayInsertAt, arrayMove } from 'shared/utils/array'
import FreeSettingsDrawer, {
  IFreeSettingsDrawer
} from 'modals/free/FreeSettingsDrawer'

const Free = () => {
  const navigate = useNavigate()
  const { lessons } = useContext(LessonsContext)
  const [searchParams, setSearchParams] = useSearchParams()
  const { entityId } = useParams() as { entityId: string }
  const deleteModalRef = useRef<IDeleteConfirmationModal>(null)
  const c: IFree | undefined = useSelector(state => get(state.frees, entityId))
  const settingsDrawerRef = useRef<IFreeSettingsDrawer>(null)
  const [editSectionId, setEditSectionId] = useState<string>()
  const teachers = useSelector(state => state.teachers)
  const partitions = useSelector(state => state.partitions)

  const currentTabIndex = useMemo(() => {
    const tab = searchParams.get('tab')
    if (tab) {
      return +tab
    } else {
      return 0
    }
  }, [searchParams])

  const updateLessons = (ids: string[]) => {
    dbUpdateFree(entityId, { lessons: ids })
  }

  const onDuplicateLesson = (lessonId: string) => {
    const l = lessons && lessons[lessonId]
    if (l && c) {
      const id = generateId()
      const newL: ILesson = {
        ...l,
        id,
        name: l.name + ' (copy)',
        entityId: c.id
      }
      dbCreateLesson(id, newL)
      dbUpdateFree(entityId, { lessons: [...(c?.lessons || []), id] })
    }
  }

  const onAddLesson = () => {
    if (c) {
      const id = generateId()
      const newL: ILesson = {
        id,
        name: 'Новый урок',
        entityId: c.id,
        type: ILessonType.LECTURE
      }
      dbCreateLesson(id, newL)
      dbUpdateFree(entityId, { lessons: [...(c?.lessons || []), id] })
    }
  }

  const onDeleteLesson = (lessonId: string) => {
    if (c) {
      const newIds = filter(c.lessons, lId => lId !== lessonId)
      dbUpdateFree(entityId, { lessons: newIds })
      dbDeleteLessonEntity(lessonId, c.id)
    }
  }

  const onLessonClick = (id: string) => {
    navigate(`/free/${entityId}/${id}`)
  }

  const onDelete = () => {
    console.log('deleteCourse')
    if (c) {
      dbDeleteFree(c)
      navigate('/free')
    }
  }

  const onDuplicate = async () => {
    if (c && lessons) {
      const newCourseId = await dbDuplicateFree(c, lessons)
      navigate(`/free/${newCourseId}`)
    }
  }

  const onAddSection = (sType: SectionType, i: number) => {
    if (c) {
      const s = createSection(sType)
      const newSections = {
        ...(c.sections || {}),
        [s.id]: s
      }
      const newSectionsOrder = arrayInsertAt(c.sectionsOrder || [], i, s.id)
      const upd = {
        sections: newSections,
        sectionsOrder: newSectionsOrder
      }
      dbUpdateFree(c.id, upd)
      setEditSectionId(s.id)
    }
  }

  const onMoveSection = (fromIndex: number, toIndex: number) => {
    console.log('onMoveSection', fromIndex, toIndex)
    if (c && c.sectionsOrder) {
      const newSectionsOrder = arrayMove(c.sectionsOrder, fromIndex, toIndex)
      const upd = {
        sectionsOrder: newSectionsOrder
      }
      dbUpdateFree(c.id, upd)
    }
  }

  const updateSection = (s: ISection) => {
    if (c) {
      const upd = {
        sections: {
          ...(c.sections || {}),
          [s.id]: s
        }
      }
      dbUpdateFree(c.id, upd)
    }
  }

  const onEditSection = (sId: string) => {
    setEditSectionId(sId)
  }

  const onDeleteSection = () => {
    if (c && editSectionId) {
      const newOrder = filter(c.sectionsOrder, lId => lId !== editSectionId)
      const newSections = { ...c.sections }
      delete newSections[editSectionId]
      setEditSectionId(undefined)
      const upd = { sections: newSections, sectionsOrder: newOrder }
      dbUpdateFree(c.id, upd)
    }
  }

  const onDuplicateSection = () => {
    if (c && editSectionId && c.sections && c.sectionsOrder) {
      const s = c.sections[editSectionId]
      const index = c.sectionsOrder.indexOf(editSectionId)
      const id = generateId()
      const newS: ISection = {
        ...s,
        id
      }
      const newSections = {
        ...c.sections,
        [newS.id]: newS
      }
      const newSectionsOrder = arrayInsertAt(
        c.sectionsOrder || [],
        index + 1,
        newS.id
      )
      const upd = { sections: newSections, sectionsOrder: newSectionsOrder }
      dbUpdateFree(c.id, upd)
      setEditSectionId(id)
    }
  }

  const navBarActions = (
    <Menu>
      <MenuButton as={IconButton} icon={<SettingsIcon />}>
        Actions
      </MenuButton>
      <MenuList>
        <MenuItem icon={<CopyIcon />} onClick={onDuplicate}>
          Создать копию
        </MenuItem>
        <MenuItem
          color='red'
          icon={<DeleteIcon />}
          onClick={() => deleteModalRef.current?.open()}
        >
          Удалить
        </MenuItem>
      </MenuList>
    </Menu>
  )

  const renderTabs = () => {
    return (
      <TabList
        display='flex'
        alignItems='center'
        justifyContent='center'
        p='4'
        overflow={'hidden'}
      >
        <Tab>Страница курса</Tab>
        <Tab>Уроки</Tab>
        <Tab>Ученики</Tab>
      </TabList>
    )
  }

  if (!c) {
    return <Loading />
  } else {
    return (
      <VStack
        w='full'
        h='full'
        spacing={0}
        overflow={'hidden'}
        maxH={'full'}
        sx={{ '--chakra-zIndices-modal': '2' }}
      >
        <Tabs
          size='sm'
          isLazy
          colorScheme='wood'
          variant='soft-rounded'
          w='full'
          overflow={'hidden'}
          h='full'
          flex={1}
          p={0}
          display={'flex'}
          flexDirection={'column'}
          index={currentTabIndex}
          onChange={(tabIndex: number) =>
            setSearchParams({ tab: tabIndex.toString() })
          }
        >
          <NavBar title={c.title} tabs={renderTabs()}>
            {navBarActions}
          </NavBar>

          <TabPanels overflow={'hidden'} flex={1}>
            <CustomTabPanel bg='black.950'>
              <VStack
                w='full'
                bg={'wood.100'}
                borderBottomRadius={{ base: 'sm', lg: 'lg' }}
                overflowY={'auto'}
              >
                <VStack
                  w='full'
                  justify={'flex-start'}
                  flex={1}
                  spacing={0}
                  h='full'
                  overflowX={'hidden'}
                  overflowY={'auto'}
                  minH='full'
                  bg='black.950'
                >
                  <VStack
                    w='full'
                    bg={'wood.100'}
                    borderBottomRadius={{ base: 'sm', lg: 'lg' }}
                  >
                    <FreePage
                      storageRef={storageRef}
                      course={c}
                      onApplyClick={() => undefined}
                      admin={{
                        onAddSection,
                        onMoveSection,
                        onEditSection,
                        onEditHeader: () =>
                          settingsDrawerRef.current?.open(c.id)
                      }}
                      teachers={teachers || {}}
                      partitions={partitions}
                    />
                  </VStack>
                  <Footer />
                </VStack>
              </VStack>
            </CustomTabPanel>
            <CustomTabPanel>
              {c.lessons && c.lessons.length > 0 ? (
                <LessonsList
                  ids={c.lessons}
                  updateIds={updateLessons}
                  onAddLesson={onAddLesson}
                  onDuplicate={onDuplicateLesson}
                  onDelete={onDeleteLesson}
                  onClick={onLessonClick}
                />
              ) : (
                <EntityContent
                  content={c.content || []}
                  entityType={IEntityType.FREE}
                  entityId={c.id}
                />
              )}
            </CustomTabPanel>
            <CustomTabPanel>
              <EntityUsers />
            </CustomTabPanel>
          </TabPanels>
        </Tabs>
        <DeleteConfirmation
          ref={deleteModalRef}
          title='Удаление курса'
          description={`Вы собираетесь удалить курс "${c.title}" и все его уроки. Курс станет не доступен для пользователей купивших его.`}
          onDelete={onDelete}
        />
        <FreeSettingsDrawer ref={settingsDrawerRef} />
        <EditSectionDrawer
          s={editSectionId && get(c, ['sections', editSectionId])}
          onChange={updateSection}
          onClose={() => setEditSectionId(undefined)}
          entityId={c.id}
          onDelete={onDeleteSection}
          onDuplicate={onDuplicateSection}
        />
      </VStack>
    )
  }
}

export default Free
